import { PageLoading } from "@ant-design/pro-layout";
import { ErrorBoundary } from "@ant-design/pro-utils";
import { useStore } from "contexts";
import MachineLayout from "layouts/machine";
import { FC, Suspense, lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import dashboardRoutes from "routes/dashboard";
import auhtRoutes from "./auth";
import machineRoutes from "./machine";
import { IRoute } from "./types";

const AuthLayout = lazy(() => import("layouts/auth"));
const DashboardLayout = lazy(() => import("layouts/dashboard"));
const NotAuthorized = lazy(() => import("pages/exceptions/NotAuthorized"));
const NotFound = lazy(() => import("pages/exceptions/NotFound"));

const MainRoutes: FC = () => {
  const location = useLocation();
  const [{ authorized }] = useStore();

  const routes: IRoute[] = [
    {
      path: "/auth",
      key: "auth",
      component: <AuthLayout />,
      children: auhtRoutes,
    },
    {
      path: "/403",
      key: "not_authorized",
      component: <NotAuthorized />,
    },
    {
      key: "not_found",
      path: "*",
      component: <NotFound />,
    },
  ];

  if (authorized) {
    routes.push(
      {
        path: "/dashboard",
        key: "dashboard",
        component: <DashboardLayout />,
        children: dashboardRoutes,
      },
      {
        path: "/machine",
        key: "machine",
        component: <MachineLayout />,
        children: machineRoutes,
      }
    );
  }

  return (
    <TransitionGroup>
      <CSSTransition key={location.key} classNames="fade" timeout={300}>
        <Routes location={location}>
          <Route
            key="root"
            path="/"
            element={
              authorized ? (
                <Navigate to={"/machine/choice"} />
              ) : (
                <Navigate to={"/auth/login"} />
              )
            }
          />
          {routes.map((route) =>
            !route.children ? (
              <Route
                key={route.key}
                path={route.path}
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      {route.component}
                    </Suspense>
                  </ErrorBoundary>
                }
              />
            ) : (
              <Route
                key={route.key}
                path={route.path}
                element={
                  <ErrorBoundary>
                    <Suspense fallback={<PageLoading />}>
                      {route.component}
                    </Suspense>
                  </ErrorBoundary>
                }
              >
                {route.children.map((item) => (
                  <Route
                    key={item.key}
                    path={item.path}
                    element={
                      <ErrorBoundary>
                        <Suspense fallback={<PageLoading />}>
                          {item.component}
                        </Suspense>
                      </ErrorBoundary>
                    }
                  />
                ))}
              </Route>
            )
          )}
        </Routes>
      </CSSTransition>
    </TransitionGroup>
  );
};

export default MainRoutes;
